import { RoutedViewProps } from "@my-dld/utils/UrlUtils";
import React, { Component } from "react";
import { hideLoading, showErrorMessage, showLoading } from "@my-dld/utils/UIUtils";
import { getPropertiesRowSearchQuery, getPropertiesSearchQuery } from "@eres/ejari-api/api";
import EjariApi from "@eres/ejari-api";
import { ProjectProperty, PropertiesWithProject, RentalStatus } from "@eres/ejari-api/Entity/LeasedProperties";
import Strings from "@my-dld/assets/strings";
import { EjariPropertyType } from "@eres/ejari-api/Entity";
import { I8N } from "@eres/core";
import { navigateWithInParent } from "@my-dld/utils/StoreUtils";
import { switchEjariUserMode } from "@eres/ejari-core/contexts/EjariContext";
import { LeasedContext, updateLeasedContext } from "@eres/ejari-core/contexts/LeasedContext";
import { AppConstants } from "../../../../configApp";
import ListGridDataView from "@my-dld/components/DataView/ListGridDataView";
import ProjectPropertiesListView from "../../component/Leased/ProjectPropertiesListView";
import ProjectPropertiesGridView from "../../component/Leased/ProjectPropertiesGridView";
import { isEmptyStringOrNull } from "@eres/core/dist/StringUtils";
import { GRID_SIZE } from "@my-dld/components/DataView/GridDataView";
import { showAddPropertySearch } from "@10x/addproperty/NavigationUtils";
import { isOwnerLoggedIn } from "@10x/sso";
import { Gated } from "@10x/feature-gate";

interface Props extends RoutedViewProps<{ rentalStatus: string }> {

}

interface State {
	isLoading: boolean;
	properties?: ProjectProperty[];
	status: string;
	startIndex: number;
	totalRows: number;
}

class ProjectProperties extends Component<Props, State> {
	static contextType = LeasedContext;
	context!: React.ContextType<typeof LeasedContext>;

	constructor( props: Props ) {
		super( props );
		this.state = {
			isLoading: true,
			status: props.match.params.rentalStatus,
			startIndex: 0,
			totalRows: 0,
		};
	}

	componentDidMount(): void {
		const { project, properties } = this.context;
		if ( project && properties ) {
			this.setState( {
				isLoading: false,
				properties: properties,
				totalRows: properties[ 0 ].TotalRecords!,
			} );
		} else {
			this.loadProjectProperties();
		}
	}

	render() {
		const { project } = this.context;
		const { isLoading, properties, totalRows } = this.state;
		return <ListGridDataView
			isLoading={ isLoading }
			dataAvailable={ properties && properties.length > 0 }
			title={ Strings.dash_ejari_properties_title }
			renderListView={ this.renderListView }
			renderGridView={ this.renderGridView }
			extraController={ this.renderExtraController }
			totalCount={ totalRows }
			onPageChange={ this.onPageChange }>
			{ project && <>
				{ <RenderProject project={ project }/> }
				{ project.ProjectNumber == null && isOwnerLoggedIn() &&
					<Gated featureId={ "addPropertyOnProject" }>
						<div className="text-end mb-3">
							<button
								type="button"
								className="btn btn_1 btn_style1 font-size14"
								onClick={ this.onAddProperty }>{ Strings.didnt_find_property }</button>
						</div>
					</Gated>
				}
			</> }
		</ListGridDataView>;
	}

	private renderListView = () => {
		const { properties } = this.state;
		return <ProjectPropertiesListView
			properties={ properties ? properties : [] }
			onPropertySelect={ this.onPropertySelect }/>;
	};

	private renderGridView = () => {
		const { properties } = this.state;
		return <ProjectPropertiesGridView
			properties={ properties ? properties : [] }
			onPropertySelect={ this.onPropertySelect }/>;
	};

	private renderExtraController = () => {
		const { propertySearch } = this.context;
		const { status } = this.state;
		const searchStatus = ( propertySearch && propertySearch.PropertyStatus ) ? propertySearch.PropertyStatus : undefined;
		return <React.Fragment>
			<RenderChangeableRentalStatus
				status={ status }
				onStatusChange={ this.onStatusChange }
				searchStatus={ searchStatus }/>
			<RenderRentalStatus searchStatus={ searchStatus }/>
		</React.Fragment>;
	};

	private onPageChange = ( pageIndex: number ) => {
		this.setState( {
			startIndex: ( ( pageIndex - 1 ) ), // GRID_SIZE * ( pageIndex - 1 )
		}, () => {
			showLoading();
			this.loadProjectProperties();
		} );
	};

	private onStatusChange = ( status: string ) => {
		this.setState( { status: status, startIndex: 0 }, () => {
			showLoading();
			this.loadProjectProperties();
		} );
	};

	private loadProjectProperties() {
		const { startIndex, status } = this.state;
		const { propertySearch, project } = this.context;
		if ( project ) {
			//const searchQuery = getPropertiesSearchQuery( project.ProjectId!, startIndex, status, propertySearch );
			const searchQuery = getPropertiesRowSearchQuery(  startIndex, status, project.RowValue);
			EjariApi.getProjectProperties( searchQuery ).then( ( resp: any ) => {
				hideLoading();
				const properties: ProjectProperty[] = ( resp ? resp.Response : [] );
				this.setState( {
					isLoading: false,
					properties: properties,
					totalRows: properties.length > 0 ? properties.length : 0,
				} );
			} ).catch( e => {
				this.setState( { isLoading: false } );
				hideLoading();
				showErrorMessage( e );
			} );
		}
	}

	private onPropertySelect = ( property: ProjectProperty ) => {
		updateLeasedContext( { properties: this.state.properties, property: property, units: undefined } );
		if ( property.PropertyTypeId !== EjariPropertyType.VILLA ) {
			const propertySearch = this.context.propertySearch;
			const rentalStatus = ( propertySearch && propertySearch.PropertyStatus ) ? propertySearch.PropertyStatus : RentalStatus.ALL;
			navigateWithInParent( "mydld", `leased/units/${ rentalStatus }` );
		} else {
			switchEjariUserMode( "owner" );
			navigateWithInParent( "mydld",
				`leased/property/${ property.PropertyTypeId }/${ encodeURIComponent( property.RowValue ?? "" ) }/${ property.IsFreeHold }/${ true }/ejarisvc` ); // property.PropertyIdKey
		}
	};

	private onAddProperty = () => {
		updateLeasedContext( { properties: this.state.properties } );
		showAddPropertySearch();
	};
}

function RenderProject( props: { project: PropertiesWithProject } ) {
	const { project } = props;
	const projectIcon = `${ AppConstants.rootUrl }assets/property-icons/building.svg`;
	return <div>
		{ project.ProjectNumber && <div className="property-info-summary">
			<div>
				<div className="property-icon">
					<img src={ projectIcon }/>
				</div>
			</div>
			<div>
				<span>{ project.ProjectNumber }</span>
				<span>{ Strings.project_number }</span>
			</div>
			<div>
				<span>{ I8N.isRTL() ? project.ProjectNameAr : project.ProjectNameEn }</span>
				<span>{ Strings.Project_Name_Label }</span>
			</div>
			<div>
				<span>{ I8N.isRTL() ? project.DeveloperNameAr : project.DeveloperNameEn }</span>
				<span>{ Strings.developer_name }</span>
			</div>
			<div>
				<span>{ project.ProjectPropCount }</span>
				<span>{ Strings.no_of_properties }</span>
			</div>
		</div> }
		{ project.ProjectNumber == null && <div className="property-info-summary">
			<div>
				<div className="property-icon">
					<img src={ projectIcon }/>
				</div>
			</div>
			<div>
				<span>{ Strings.other_properties }</span>
			</div>
			<div>
				<span>{ project.ProjectPropCount }</span>
				<span>{ Strings.no_of_properties }</span>
			</div>
		</div> }
	</div>;
}

export function RenderRentalStatus( props: { searchStatus?: number } ) {
	const { searchStatus } = props;
	if ( searchStatus && searchStatus !== RentalStatus.ALL ) {
		return <div className="btn-group btn-toggle btn-custom-toggle ">
			{ searchStatus === RentalStatus.RENTED &&
				<button type="button" className={ "btn btn-sm active" }>{ Strings.rented }</button> }
			{ searchStatus === RentalStatus.VACANT &&
				<button type="button" className={ "btn btn-sm active" }>{ Strings.vacant }</button> }
		</div>;
	}
	return null;
}

export function RenderChangeableRentalStatus( props: { status: string, onStatusChange: ( status: string ) => void, searchStatus?: number } ) {
	const { status, onStatusChange, searchStatus } = props;
	if ( searchStatus == undefined || ( searchStatus && searchStatus === RentalStatus.ALL ) ) {
		return <div className="btn-group btn-toggle btn-custom-toggle ">
			<button
				type="button"
				className={ "btn btn-sm " + ( status === "" + RentalStatus.ALL ? "active" : "" ) }
				onClick={ () => onStatusChange( "" + RentalStatus.ALL ) }>{ Strings.all }</button>
			<button
				type="button"
				className={ "btn btn-sm " + ( status === "" + RentalStatus.RENTED ? "active" : "" ) }
				onClick={ () => onStatusChange( "" + RentalStatus.RENTED ) }>{ Strings.rented }</button>
			<button
				type="button"
				className={ "btn btn-sm " + ( status === "" + RentalStatus.VACANT ? "active" : "" ) }
				onClick={ () => onStatusChange( "" + RentalStatus.VACANT ) }>{ Strings.vacant }</button>
		</div>;
	}
	return null;
}

export function getProjectPropertyArea( property: ProjectProperty ) {
	const name = I8N.isRTL() ? property.LandAreaNameAr : property.LandAreaNameEn;
	return !isEmptyStringOrNull( name ) ? name! : "-";
}

export function getProjectPropertyBuilding( property: ProjectProperty ) {
	const name = I8N.isRTL() ? property.BuildingNameAr : property.BuildingNameEn;
	return !isEmptyStringOrNull( name ) ? name! : "-";
}

export function getProjectPropertyBuildingCount( property: ProjectProperty ) {
	if ( property.BuildingUnitsCount )
		return property.BuildingUnitsCount + " " + Strings.units;
	return "";
}

export function getProjectPropertyType( property: ProjectProperty ) {
	let type = I8N.isRTL() ? property.EjariTypeNameAr : property.EjariTypeNameEn;
	type = !isEmptyStringOrNull( type ) ? type : "";
	const subType = I8N.isRTL() ? property.EjariSubTypeNameAr : property.EjariSubTypeNameEn;
	if ( !isEmptyStringOrNull( subType ) ) {
		type += ", " + subType;
	}
	if ( property.Rooms != undefined && property.Rooms !== 0 ) {
		type += property.Rooms + " " + Strings.rooms;
	}
	return type!;
}

export default ProjectProperties;
